import React, { FC, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import CommonButton from "components/common/ui/CommonButton";
import dayjs from "dayjs";
import SpacedTextInput from "components/common/ui/SpacedTextInput";

interface SpecialMealDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, dateAndTime: string) => void;
  servingDate: string;
  timezone?: string;
}

const SpecialMealDialog: FC<SpecialMealDialogProps> = ({
  open,
  onClose,
  onConfirm,
  servingDate,
  timezone: siteTimezone,
}) => {
  const [reason, setReason] = useState("");

  // servingDate arrives as "YYYY-MM-DDTHH:mm:ss±HH:MM" in the site's timezone.
  // Strip the offset so the wall-clock value (what the database stored) round-
  // trips naive — sending and displaying through dayjs without slicing would
  // re-interpret the time in the browser's local timezone.
  const tzNaiveServingDate = servingDate.slice(0, 19);

  const handleConfirm = (): void => {
    if (reason.trim()) {
      onConfirm(reason.trim(), tzNaiveServingDate);
      setReason("");
      onClose();
    }
  };

  const handleCancel = (): void => {
    setReason("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Make Special Meal</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <SpacedTextInput
            fullWidth
            multiline
            rows={4}
            label="Reason for Special Meal"
            placeholder="Enter the reason why this meal should be special..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            variant="outlined"
            required
          />
          <Box sx={{ mt: 1, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <strong>Special Date:</strong>{" "}
            {dayjs(tzNaiveServingDate).format("dddd, MMMM DD, YYYY")}
          </Box>
          <Box sx={{ mt: 1, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <strong>Serving Time:</strong>{" "}
            {dayjs(tzNaiveServingDate).format("HH:mm")}
            {siteTimezone &&
            siteTimezone !== Intl.DateTimeFormat().resolvedOptions().timeZone
              ? ` (${siteTimezone})`
              : ""}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <CommonButton text="Cancel" onClick={handleCancel} />
        <CommonButton
          text="Confirm"
          positive={!!reason.trim()}
          onClick={handleConfirm}
          disabled={!reason.trim()}
        />
      </DialogActions>
    </Dialog>
  );
};

export default SpecialMealDialog;
